import { defineComponent as _defineComponent } from 'vue'
import { withKeys as _withKeys, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue"

import { computed, nextTick, onUnmounted, ref, watch } from "vue"
import { useViewFamilyStore } from "@/rd/ViewFamilyStore"
import { useViewPersonStore } from "@/rd/ViewPersonStore"
import { livingWindowStart, ViewFamily, ViewPerson } from "@/rd/ResearchDataModel"
import { onClickOutside } from "@vueuse/core"
import { sortBy } from "lodash"
import AutoCompleteInput from "@/util/AutoCompleteInput.vue"
import FamilyListItem from "@/manage/FamilyListItem.vue"
import { LoadMode } from "@/util/AsyncData"

interface FamilyResult {
  value: string
  father?: ViewPerson
  mother?: ViewPerson
}

export default /*#__PURE__*/_defineComponent({
  __name: 'ViewFamilySelector',
  props: {
    modelValue: {},
    canEdit: { type: Boolean },
    editEmpty: { type: Boolean },
    placeholder: {},
    emptyText: {},
    recentLivingOnly: { type: Boolean },
    textOnly: { type: Boolean }
  },
  emits: [
  'update:modelValue',
  'inputFocusChanged',
],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





__expose({
  showSearchInput,
  focus: () => inputRef.value?.focus(),
})

const showInputRef = ref(false)

const viewFamilyStore = useViewFamilyStore()
const viewPersonStore = useViewPersonStore()

const viewFamilyRef = computed(() => viewFamilyStore.getAsyncFamily(props.modelValue, LoadMode.EnsureLoaded)?.data)
const fatherRef = computed(() => viewPersonStore.getLoadedPerson(viewFamilyRef.value?.fatherId))
const motherRef = computed(() => viewPersonStore.getLoadedPerson(viewFamilyRef.value?.motherId))
const displayTextRef = computed(() => {
  if (viewFamilyRef.value) {
    return ViewFamily.getDisplayName(fatherRef.value, motherRef.value)
  }
  if (props.modelValue) {
    return "(deleted or not visible)"
  }
  return props.emptyText ?? "None selected"
})
const placeholderRef = computed(() => props.placeholder ?? "type a spouse's name")
const defaultShowInputRef = computed(() => !props.modelValue)

const selectorRef = ref<HTMLElement>()
const inputRef = ref<InstanceType<typeof AutoCompleteInput>>()
let stopClickOutside: (() => void) | undefined

onUnmounted(onInputBlur) // ensure clickOutside cleanup

function onInputFocus() {
  console.log('ViewFamilySelector.InputFocus')
  stopClickOutside = onClickOutside(selectorRef, e => {
    const target = e.target as HTMLElement
    if (!inputRef.value?.isDropdownElement(target)) {
      showInputRef.value = defaultShowInputRef.value
    }
  })
  emit('inputFocusChanged', true)
}

function onInputBlur() {
  console.log('ViewFamilySelector.InputBlur')
  stopClickOutside?.()
  stopClickOutside = undefined
  showInputRef.value = defaultShowInputRef.value
  emit('inputFocusChanged', false)
}

watch(() => props.modelValue, () => showInputRef.value = defaultShowInputRef.value, { immediate: true })

watch(showInputRef, async value => {
  await nextTick()
  if (!value && !inputRef.value) {
    console.log('ViewFamilySelector.InputRemoved')
    onInputBlur() // fake blur when input removed from DOM
  }
}, { immediate: true })

function onEscapeKey() {
  showInputRef.value = false
}

async function showSearchInput() {
  showInputRef.value = true
  await nextTick()
  inputRef.value?.focus()
}

async function toggleShowSearchInput() {
  if (showInputRef.value) {
    showInputRef.value = false
  }
  else {
    showInputRef.value = true
    await nextTick()
    inputRef.value?.focus()
  }
}

async function searchFamilies(query: string) {
  const names = query.replace(',', ' ').split(' ').map(n => n.trim()).filter(n => n.length > 0)
  if (!names.some(n => n.length >= 3))
    return []

  const asyncViewFamilies = await viewFamilyStore.searchByNamesAsync(names)
  let results = asyncViewFamilies.map(a => a.data!).map(vf => {
    const father = viewPersonStore.getLoadedPerson(vf.fatherId) // loaded with family
    const mother = viewPersonStore.getLoadedPerson(vf.motherId) // loaded with family
    const name = ViewFamily.getDisplayName(father, mother)
    return {
      value: vf.id!,
      name,
      father,
      mother,
    } as FamilyResult
  })
  if (props.recentLivingOnly) {
    results = results.filter(fr => {
      // allow creating child-to-parent relationships to parents born close to living window
      return fr.father?.recentlyLiving ?? fr.mother?.recentlyLiving ?? true // assume recent living if no dates
    })
  }
  return sortBy(results, "name")
}

function familySelected(item: FamilyResult) {
  showInputRef.value = false
  emit('update:modelValue', item.value)
}


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "selectorRef",
    ref: selectorRef,
    class: "view-family-selector"
  }, [
    (showInputRef.value)
      ? (_openBlock(), _createBlock(AutoCompleteInput, {
          key: 0,
          ref_key: "inputRef",
          ref: inputRef,
          placeholder: placeholderRef.value,
          search: searchFamilies,
          "item-component": FamilyListItem,
          "get-component-props": (item) => ({ familyId: item.value, father: item.father, mother: item.mother }),
          onFocus: onInputFocus,
          onBlur: onInputBlur,
          onKeydown: _withKeys(onEscapeKey, ["esc"]),
          onResultSelected: familySelected
        }, null, 8, ["placeholder", "get-component-props", "onKeydown"]))
      : (_ctx.canEdit)
        ? (_openBlock(), _createElementBlock("button", {
            key: 1,
            type: "button",
            class: _normalizeClass(["btn btn-inline btn-link link-dark", { none: !props.modelValue }]),
            onClick: toggleShowSearchInput
          }, [
            (_ctx.textOnly || !_ctx.modelValue)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(_toDisplayString(displayTextRef.value), 1)
                ], 64))
              : (_openBlock(), _createBlock(FamilyListItem, {
                  key: 1,
                  "family-id": _ctx.modelValue,
                  father: fatherRef.value,
                  mother: motherRef.value
                }, null, 8, ["family-id", "father", "mother"]))
          ], 2))
        : (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: _normalizeClass(["input-value", { none: !viewFamilyRef.value }])
          }, _toDisplayString(displayTextRef.value), 3))
  ], 512))
}
}

})